import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  field: 'label' | 'briefing';
  fieldLabel: string;
  language: string;
  text: string;
  editable: boolean;
  onSave: (next: string) => Promise<void>;
}

export default function CatalogDiffBlock({ field, fieldLabel, language, text, editable, onSave }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(text);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(text);
  }, [open, text]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(draft);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {fieldLabel} <span className="font-mono">[{language}]</span>
        </span>
        {editable && (
          <Button variant="ghost" size="xs" onClick={() => setOpen(true)}>
            <Pencil className="size-3" />
            {t('superuser.catalog.diff.edit', { defaultValue: 'Editar' })}
          </Button>
        )}
      </div>
      <p className={`whitespace-pre-wrap text-sm ${field === 'briefing' ? 'text-muted-foreground' : 'text-foreground'}`}>
        {text || <span className="italic text-muted-foreground/60">—</span>}
      </p>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('superuser.catalog.diff.editTitle', {
                field: fieldLabel,
                lang: language,
                defaultValue: 'Editar {{field}} ({{lang}})',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('superuser.catalog.diff.editDescription', {
                defaultValue: 'Edita la traducción IA. El cambio se guarda en el borrador.',
              })}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={field === 'briefing' ? 10 : 2}
              className={field === 'briefing' ? 'min-h-[12rem]' : ''}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="size-4 animate-spin" />}
                {t('superuser.catalog.form.save', { defaultValue: 'Guardar' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
